import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { FullCountryObject, countries } from "assets/data/countries";
import { languageObject, languagesArray } from "assets/data/languages";
import { SubTopicObject, subtopics } from "assets/data/topics";
import { SetStateAction, useEffect, useState } from "react";
import {
    Avatar,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";

import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { CustomComponentProps, NotiObj } from "utils/types/general.types";
import { logo } from "utils/assets_exports/images";
import { useAppDispatch } from "services/redux/store";
import { removeNoti, updateNoti } from "services/redux/notifications";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1
});

export type EditNotiFormProps = CustomComponentProps & {
    showTitle?: boolean;
    noti?: NotiObj;
    setEditNoti?: React.Dispatch<SetStateAction<NotiObj | undefined>>;
    handleSave?: Function;
    handleCloseModal?: boolean;
    handleClose?: Function;
};

const EditNotiForm = ({
    showTitle = false,
    noti,
    setEditNoti,
    handleSave,
    handleCloseModal = false,
    handleClose,
    className,
    style
}: EditNotiFormProps) => {
    const dispatch = useAppDispatch();

    const [selectedCountry, setSelectedCountry] = useState<FullCountryObject[]>(
        []
    );

    const [selectedLanguage, setSelectedLanguage] = useState<languageObject[]>(
        []
    );

    const [selectedTopics, setSelectedTopics] = useState<SubTopicObject[]>([]);

    const [selectedImage, setSelectedImage] = useState<string | null>();

    const handleFileChange = (file: FileList | null) => {
        if (!file) {
            return;
        }
        setSelectedImage(URL.createObjectURL(file[0]));
    };

    const [title, setTitle] = useState("");
    const [titleError, setTitleError] = useState(false);
    useEffect(() => {
        if (title.length && titleError) {
            setTitleError(false);
        }
    }, [title]);
    const [desc, setDesc] = useState("");
    const [descError, setDescError] = useState(false);
    useEffect(() => {
        if (desc.length && descError) {
            setDescError(false);
        }
    }, [desc]);

    const [timeSelect, setTimeSelect] = useState<"now" | "schedule">(
        "schedule"
    );
    const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTimeSelect(
            (event.target as HTMLInputElement).value as "now" | "schedule"
        );
    };

    const [scheduleValue, setScheduleValue] = useState<Dayjs | null>(dayjs());

    useEffect(() => {
        if (noti) {
            setSelectedCountry(noti.country || []);
            setSelectedLanguage(noti.language || []);
            setSelectedTopics(noti.topics ? noti.topics : []);
            setSelectedImage(noti.media);
            setTitle(noti.title);
            setDesc(noti.message);
            setTimeSelect("schedule");
            setScheduleValue(dayjs(noti.scheduleDate));
        } else {
            setSelectedCountry([]);
            setSelectedLanguage([]);
            setSelectedTopics([]);
            setSelectedImage(null);
            setTitle("");
            setDesc("");
            setTimeSelect("schedule");
            setScheduleValue(dayjs());
        }
    }, [noti]);

    const clearForm = () => {
        setTitle("");
        setDesc("");
        setTitleError(false);
        setDescError(false);
        setScheduleValue(null);
        setTimeSelect("now");
        setSelectedCountry([]);
        setSelectedLanguage([]);
        setSelectedTopics([]);
        setSelectedImage(null);
    };

    useEffect(() => {
        if (handleCloseModal) {
            clearForm();
            setEditNoti && setEditNoti(undefined);
        }
    }, [handleCloseModal]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!noti) {
            return;
        }

        if (!title.length) {
            setTitleError(true);
            return;
        }

        if (!desc.length) {
            setDescError(true);
            return;
        }

        let media: string = logo;
        if (selectedImage) {
            media = selectedImage;
        }

        let scheduleDate = dayjs().toString();
        if (scheduleValue) {
            scheduleDate = scheduleValue.toString();
        }

        let country: FullCountryObject[] = [];
        if (selectedCountry) {
            country = selectedCountry;
        }

        let language: languageObject[] = [];
        if (selectedLanguage) {
            language = selectedLanguage;
        }
        const editNoti: NotiObj = {
            id: noti.id,
            title,
            message: desc,
            notiType: noti.notiType,
            media,
            createDate: noti.createDate,
            scheduleDate,
            status: scheduleValue ? "pending" : "now",
            reminder: noti.reminder,
            country,
            language,
            topics: selectedTopics
        };

        dispatch(updateNoti(editNoti));
        clearForm();
        handleSave && handleSave();
    };

    const handleDelete = () => {
        if (!noti) {
            return;
        }
        dispatch(removeNoti(noti.id));
        clearForm();
        setEditNoti && setEditNoti(undefined);
        handleClose && handleClose();
    };

    return (
        <form
            className={`flex flex-col justify-start items-center gap-5 ${className}`}
            style={style}
            onSubmit={handleSubmit}
        >
            {showTitle && (
                <div className="flex justify-center items-center py-2 px-4">
                    <h1 className="dark:text-white">Edit Push Notification</h1>
                </div>
            )}

            {/* countries */}
            <Autocomplete
                // key={selectedCountry?.name.common || "select.country"}
                multiple
                disablePortal
                value={selectedCountry}
                onChange={(_event, newValue) => {
                    setSelectedCountry(newValue);
                }}
                options={countries}
                getOptionLabel={(option: FullCountryObject) =>
                    option.name.common
                }
                className="w-full"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select Country"
                        placeholder="Country"
                    />
                )}
            />
            {/* languages */}
            <Autocomplete
                disablePortal
                multiple
                // key={selectedLanguage?.name || "select.langauge"}
                value={selectedLanguage}
                onChange={(_event, newValue) => {
                    setSelectedLanguage(newValue);
                }}
                options={languagesArray}
                getOptionLabel={(option: languageObject) => option.name}
                className="w-full"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select Language"
                        placeholder="Langugae"
                    />
                )}
            />
            {/* topics */}
            <Autocomplete
                multiple
                options={subtopics}
                getOptionLabel={(option) => option.Name}
                // defaultValue={[top100Films[13]]}

                value={selectedTopics}
                onChange={(_event, newValues) => {
                    setSelectedTopics(newValues);
                }}
                filterSelectedOptions
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Selected Topics"
                        placeholder="Topics"
                    />
                )}
                className="w-full"
            />

            {/* select media */}
            <div className="flex w-full justify-start gap-10 items-center">
                <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    style={{ color: "white" }}
                >
                    Upload file
                    <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e.target.files)}
                    />
                </Button>

                {selectedImage && (
                    <Avatar
                        alt="Notification media"
                        src={selectedImage}
                        sx={{ width: 56, height: 56 }}
                    />
                )}
            </div>

            {/* title */}
            <TextField
                name="title"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                variant="outlined"
                className="w-full"
                error={titleError}
                helperText={titleError ? "Please Write Title" : ""}
            />
            {/* message */}
            <TextField
                name="msg"
                label="Message"
                placeholder="Your message..."
                multiline
                rows={4}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                variant="outlined"
                className="w-full"
                error={descError}
                helperText={descError ? "Please Write The Message" : ""}
            />

            {/* time */}
            <div className="flex w-full justify-start">
                <FormControl>
                    <FormLabel id="demo-row-radio-buttons-group-label">
                        <span className="dark:text-white">
                            Notification Time
                        </span>
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={timeSelect}
                        onChange={handleTimeChange}
                    >
                        <FormControlLabel
                            value="now"
                            control={<Radio />}
                            label="Now"
                            className="dark:text-white"
                        />
                        <FormControlLabel
                            value="schedule"
                            control={<Radio />}
                            label="Schedule"
                            className="dark:text-white"
                        />
                    </RadioGroup>
                </FormControl>
            </div>
            {timeSelect === "schedule" && (
                <div className="flex w-full justify-center md:justify-start">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DateTimePicker"]}>
                            <DateTimePicker
                                label="Notification Date"
                                value={scheduleValue}
                                onChange={(newValue) =>
                                    setScheduleValue(newValue)
                                }
                            />
                        </DemoContainer>
                    </LocalizationProvider>
                </div>
            )}

            <div className="p-5 flex w-full gap-5 justify-center items-center">
                <button
                    className="px-5 py-2 text-sm text-center bg-red-600 text-white hover:shadow-xl rounded-full"
                    type="button"
                    onClick={handleDelete}
                >
                    Delete Notification
                </button>
                <button
                    className="px-5 py-2 text-sm text-center bg-momeento-50 text-white hover:shadow-xl rounded-full"
                    type="submit"
                >
                    Edit Notification
                </button>
            </div>
        </form>
    );
};

export default EditNotiForm;
